import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { INITIAL_COUNTER_SECONDS } from '@pages/Signup/Pages/PersonalDetails/VerifyPhoneNumber/PersonalDetailsStepMobileValidation'

import { useModalInstance } from '@context/ModalInstanceContext'
import { BaseModalType } from '@context/ModalsContext'

import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { BaseFieldVariant } from '@ui/components/Input/BaseField/BaseField'
import { Input } from '@ui/components/Input/Input'
import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'

import { preventPaste } from '@utils/input.utils'

import { ClientPhoneNumberDto } from '@model/AccountDetailedDto'

import { Box } from '@shared/components/Box'

interface PhoneNumberVerifyModalProps {
  phoneNumber: ClientPhoneNumberDto
  requestId: string

  verificationSuccess?: boolean

  resendCode(phoneNumber: string, phoneNumberId: string, requestId: string): void
  onReceivePhoneCall(phoneNumber: string, phoneNumberId: string, requestId: string): void

  onVerify(
    id: ClientPhoneNumberDto,
    requestId: string,
    code: string
  ): Promise<{ success: boolean; errorCode?: string; errorMessage?: string }>

  disabled?: boolean
}

type PhoneNumberVerifyFormValues = {
  phoneCode1st: string
  phoneCode2nd: string
  phoneCode3rd: string
  phoneCode4th: string
}

const fieldKeys: (keyof PhoneNumberVerifyFormValues)[] = [
  'phoneCode1st',
  'phoneCode2nd',
  'phoneCode3rd',
  'phoneCode4th',
]

const isVerificationCodeError = (verificationCodeError?: string): boolean => {
  return (
    verificationCodeError === 'phone_verification_max_attempts_reached' ||
    verificationCodeError === 'max_new_verification_code_requests_reached'
  )
}

export const PhoneNumberVerifyModal: BaseModalType<PhoneNumberVerifyModalProps> = (props) => {
  const { onVerify, phoneNumber, requestId, close } = props
  const { t } = useTranslation()
  const { setCanClose } = useModalInstance()
  const [isVerifying, setIsVerifying] = useState(false)

  useEffect(() => {
    setCanClose(false)
  }, [setCanClose])

  const [values, setValues] = useState<PhoneNumberVerifyFormValues>({
    phoneCode1st: '',
    phoneCode2nd: '',
    phoneCode3rd: '',
    phoneCode4th: '',
  })

  const code = useMemo(
    () => values.phoneCode1st + values.phoneCode2nd + values.phoneCode3rd + values.phoneCode4th,
    [values]
  )

  const [verificationError, setVerificationError] = useState<string | undefined>()
  const [verificationCodeError, setVerificationCodeError] = useState<string | undefined>()
  const [verificationSuccess, setVerificationSuccess] = useState<boolean>(
    props.verificationSuccess ?? false
  )

  const [isTimerExpired, setIsTimerExpired] = useState(false)
  const counterSpanRef = useRef<HTMLSpanElement | null>(null)
  const timerIdRef = useRef<number | null>(null)
  const counterValueRef = useRef<number>(INITIAL_COUNTER_SECONDS)

  const inputRefs = useRef<Record<keyof PhoneNumberVerifyFormValues, HTMLInputElement | null>>({
    phoneCode1st: null,
    phoneCode2nd: null,
    phoneCode3rd: null,
    phoneCode4th: null,
  })

  const lastFocusedRef = useRef<keyof PhoneNumberVerifyFormValues | null>(null)

  const setFieldValue = (name: keyof PhoneNumberVerifyFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }))
  }

  const focusField = (name: keyof PhoneNumberVerifyFormValues) => {
    lastFocusedRef.current = name
    const el = inputRefs.current[name]
    if (el) {
      el.focus()
      el.select()
    }
  }

  const focusFirstEmpty = (vals: PhoneNumberVerifyFormValues) => {
    const firstEmpty = fieldKeys.find((k) => !vals[k])
    const fieldToFocus = firstEmpty ?? fieldKeys[fieldKeys.length - 1]
    focusField(fieldToFocus)
  }

  const updateCounterLabel = useCallback(
    (seconds: number) => {
      if (!counterSpanRef.current) return

      counterSpanRef.current.textContent = t(
        'Sign up.Please allow at least 15 seconds for code to reach your phone...',
        {
          counter: `0:${String(seconds).padStart(2, '0')}`,
        }
      )
    },
    [t]
  )

  const startTimer = useCallback(() => {
    if (timerIdRef.current !== null) {
      clearInterval(timerIdRef.current)
      timerIdRef.current = null
    }

    counterValueRef.current = INITIAL_COUNTER_SECONDS
    setIsTimerExpired(false)
    updateCounterLabel(counterValueRef.current)

    timerIdRef.current = window.setInterval(() => {
      const next = counterValueRef.current - 1
      counterValueRef.current = next

      if (next <= 0) {
        updateCounterLabel(0)
        if (timerIdRef.current !== null) {
          clearInterval(timerIdRef.current)
          timerIdRef.current = null
        }
        setCanClose(true)
        setIsTimerExpired(true)
      } else {
        updateCounterLabel(next)
      }
    }, 1000)
  }, [updateCounterLabel, setCanClose])

  useEffect(() => {
    startTimer()
    return () => {
      if (timerIdRef.current !== null) {
        clearInterval(timerIdRef.current)
      }
    }
  }, [startTimer])

  useEffect(() => {
    focusFirstEmpty(values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getNextField = (name: keyof PhoneNumberVerifyFormValues) => {
    const idx = fieldKeys.indexOf(name)
    if (idx === -1) return undefined
    return fieldKeys[idx + 1]
  }

  const getPrevField = (name: keyof PhoneNumberVerifyFormValues) => {
    const idx = fieldKeys.indexOf(name)
    if (idx <= 0) return undefined
    return fieldKeys[idx - 1]
  }

  const handleDigitChange = (name: keyof PhoneNumberVerifyFormValues, digit: string) => {
    const value = digit.slice(0, 1)

    setFieldValue(name, value)

    setVerificationError(undefined)
    setVerificationCodeError(undefined)

    if (value) {
      const nextField = getNextField(name)
      if (nextField) {
        if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
          requestAnimationFrame(() => focusField(nextField))
        } else {
          setTimeout(() => focusField(nextField), 0)
        }
      }
    }
  }

  const handleBackspace = (name: keyof PhoneNumberVerifyFormValues) => {
    const currentValue = values[name]

    if (currentValue) {
      setFieldValue(name, '')
      return
    }

    const prevField = getPrevField(name)
    if (prevField) {
      setFieldValue(prevField, '')

      if (typeof window !== 'undefined') {
        const focusPrev = () => focusField(prevField)
        if ('requestAnimationFrame' in window) {
          requestAnimationFrame(focusPrev)
        } else {
          setTimeout(focusPrev, 0)
        }
      } else {
        focusField(prevField)
      }
    }
  }

  const handleResend = () => {
    const emptyValues: PhoneNumberVerifyFormValues = {
      phoneCode1st: '',
      phoneCode2nd: '',
      phoneCode3rd: '',
      phoneCode4th: '',
    }
    setValues(emptyValues)
    setVerificationError(undefined)
    setVerificationCodeError(undefined)
    startTimer()
    props.resendCode(phoneNumber.countryCode + phoneNumber.number, phoneNumber.id, requestId)
    focusFirstEmpty(emptyValues)
  }

  const handleReceivePhoneCall = () => {
    startTimer()
    props.onReceivePhoneCall(
      phoneNumber.countryCode + phoneNumber.number,
      phoneNumber.id,
      requestId
    )
  }

  const handleSubmit = async () => {
    if (isVerifying) return
    if (code.length === 4 && requestId) {
      setIsVerifying(true)
      const result = await onVerify(phoneNumber, requestId, code)

      if (!result) {
        setIsVerifying(false)
        return
      }

      if (result.success) {
        setCanClose(true)
        setVerificationSuccess(true)
        setVerificationError(undefined)
        setVerificationCodeError(undefined)
      } else {
        setVerificationSuccess(false)
        if (result.errorMessage) {
          setVerificationError(result.errorMessage)
        }
        if (result.errorCode) {
          setVerificationCodeError(result.errorCode)
        }
        setIsVerifying(false)
      }
    }
  }

  const NumberField = ({
    name,
    value,
    variant,
  }: {
    name: keyof PhoneNumberVerifyFormValues
    value: string
    variant: BaseFieldVariant
  }) => (
    <Input
      id={name}
      name={name}
      ref={(el) => {
        inputRefs.current[name] = el
      }}
      value={value ? '*' : ''}
      type='tel'
      pattern='[0-9]*'
      maxLength={1}
      autoComplete='one-time-code'
      disabled={
        props.disabled || verificationSuccess || isVerificationCodeError(verificationCodeError)
      }
      onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
        lastFocusedRef.current = name
        event.currentTarget.select()
      }}
      onPaste={preventPaste}
      onChange={(e) => {
        const digit = (e.target.value || '').replace(/\D/g, '').slice(0, 1)
        handleDigitChange(name, digit)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Backspace') {
          event.preventDefault()
          handleBackspace(name)
          return
        }

        if (!/^[0-9]$/.test(event.key) && !['Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault()
        }
      }}
      variant={variant}
    />
  )

  return (
    <Modal>
      <Modal.Header>{t('PhoneNumbers.Verify phone number')}</Modal.Header>
      <Modal.Body>
        <Box flex fullWidth align='center' gap='x16' justify='space-between'>
          <Modal.Body.Title>{t('Sign up.Enter 4-digit code')}</Modal.Body.Title>
          {verificationSuccess && (
            <FeatureIcon size='sm' icon={CircleCheckIcon} bg='surface-secondary-high-em' />
          )}
        </Box>
        <Modal.Body.Text>
          {t('Sign up.Code was sent', {
            phoneNumber: phoneNumber.countryCode + phoneNumber.number,
          })}
        </Modal.Body.Text>
        <Box flex align='center' fullWidth justify='center' gap='x4'>
          {fieldKeys.map((fieldKey) => (
            <NumberField
              key={fieldKey}
              name={fieldKey}
              value={values[fieldKey]}
              variant='digital'
            />
          ))}
        </Box>

        {!!verificationError && (
          <Modal.Body.Text color='text-danger-high-em'>{verificationError}</Modal.Body.Text>
        )}

        {isTimerExpired ? (
          <>
            <Modal.Body.Text>
              {t('Sign up.Not get code')}{' '}
              <Link size='lg' underline={false} onClick={handleResend}>
                {t('Sign up.Resend')}
              </Link>
            </Modal.Body.Text>

            <Link size='lg' underline={false} onClick={handleReceivePhoneCall}>
              {t('Sign up.Receive phone call')}
            </Link>
          </>
        ) : (
          <Modal.Body.Text ref={counterSpanRef} />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary
          disabled={!isTimerExpired}
          onClick={() => {
            close()
          }}
        >
          {t('Skip')}
        </Modal.Footer.Secondary>
        <Modal.Footer.Primary
          onClick={handleSubmit}
          disabled={code.length !== 4}
          loading={isVerifying}
        >
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
